import SaveVendorNexAliasPromptModal from '../../../modals/invoice/SaveVendorNexAliasPromptModal';
import { prepareInvoiceVendorAliasMappingsSave } from '../../../utils/prepareInvoiceVendorAliasMappingsSave';
import { findNexPoVendorMetaForName } from '../../../utils/invoiceVendorDisplay';
import {
    buildVendorAliasMappingsOnlyPayload,
    extractUpdateSettingsErrorMessage,
    readFullInvoicingSettingsFromSections,
} from '../../../utils/invoicingSettingsApi';
import SessionContext from '../../../contexts/SessionContext';
import { useContext, useMemo } from 'react';
import RevertToOriginalModal from '../../../modals/RevertToOriginalModal';
import InvoiceReconciliationDetailModal from '../../../modals/invoice/InvoiceReconciliationDetailModal';
import InvoicePartSuggestionModal from '../../../modals/invoice/InvoicePartSuggestionModal';
import InvoicePartNumberChoiceModal from '../../../modals/invoice/InvoicePartNumberChoiceModal';
import InvoiceVendorSuggestionModal from '../../../modals/invoice/InvoiceVendorSuggestionModal';
import InvoiceUnmatchConfirmModal from '../../../modals/invoice/InvoiceUnmatchConfirmModal';
import InvoiceResetConfirmModal from '../../../modals/invoice/InvoiceResetConfirmModal';
import InvoiceSubmitConfirmModal from '../../../modals/invoice/InvoiceSubmitConfirmModal';
import InvoiceReprocessPagesConfirmModal from '../../../modals/invoice/InvoiceReprocessPagesConfirmModal';
import InvoiceArithmeticBlockModal from '../../../modals/invoice/InvoiceArithmeticBlockModal';
import InvoiceChangeInvoiceTypeModal from '../../../modals/invoice/InvoiceChangeInvoiceTypeModal';
import InvoiceChangeShopLocationModal from '../../../modals/invoice/InvoiceChangeShopLocationModal';
import InvoiceChangeVendorModal from '../../../modals/invoice/InvoiceChangeVendorModal';
import InvoiceRoChangeConfirmModal from '../../../modals/invoice/InvoiceRoChangeConfirmModal';
import InvoiceTeachExtractionPromptModal from '../../../modals/invoice/InvoiceTeachExtractionPromptModal';
import type { InvoiceDetailResponse, InvoiceType } from '../../../types/invoice';
import type { useInvoiceDetailPage } from './useInvoiceDetailPage';
import type { LocationEntry } from '../../../hooks/useGetSettings';
import {
    buyPriceAfterManualUnmatch,
    computeListPriceAfterManualUnmatch,
    pricingPartKindFromNexLine,
    resolveListPriceAfterManualLkqMatch,
} from '../../../utils/applyManualMatchLkqListPrice';
import { resolveTabularLocationForGlobalPartIndex } from '../../../utils/invoiceGlobalPartIndex';
import { invoiceDetailQueryKey } from '../../../hooks/invoice/useGetInvoiceDetail';

export type InvoiceDetailPageModel = ReturnType<typeof useInvoiceDetailPage>;

export function InvoiceDetailModals({ page }: { page: InvoiceDetailPageModel }) {
    const sessionContext = useContext(SessionContext);
    const {
        id,
        invoice,
        state,
        pageReplace,
        toast,
        queryClient,
        updateSettingsMutation,
        settings,
        vendorAliasPrompt,
        setVendorAliasPrompt,
        reprocessPagesModalOpen,
        setReprocessPagesModalOpen,
        setDetailTransitionMode,
        isArithmeticBlockOpen,
        setIsArithmeticBlockOpen,
        invoiceTypeChangeModal,
        setInvoiceTypeChangeModal,
        shopLocationChangeModal,
        setShopLocationChangeModal,
        vendorChangeModal,
        setVendorChangeModal,
        roChangeConfirmOpen,
        setRoChangeConfirmOpen,
        alwaysShowFreightTab,
        salesTaxAsLineItem,
        isReturnSlip,
        isPdf,
        allImages,
        pdfPageCount,
        nexReconciliationIdentifierMode,
        nexLineQuantityBasis,
        handleConfirmInvoiceTypeChange,
        handleConfirmShopLocationChange,
        handleConfirmVendorChange,
        handleVendorChangeRequest,
        handleConfirmRoChangeSave,
        teachExtractionPrompt,
        handleCloseTeachExtractionPrompt,
        handleConfirmTeachExtractionPrompt,
        rememberObservationPending,
        extractionTeaching,
        isReadOnly,
        arithmeticFlagMessages,
    } = page;

    const invoicingSettings = useMemo(
        () =>
            readFullInvoicingSettingsFromSections(
                settings?.topSection?.sections,
                settings?.bottomSection?.sections,
            ),
        [settings],
    );

    const locationState = useMemo(() => {
        const locId = String(state.editedFields.locationId ?? invoice?.locationId ?? '').trim();
        if (!locId) return null;
        const sections = [
            ...(settings?.topSection?.sections ?? []),
            ...(settings?.bottomSection?.sections ?? []),
        ];
        const locationSection = sections.find((s) => s?.key === 'location');
        const locations = (locationSection?.data as LocationEntry[] | undefined) ?? [];
        const match = locations.find((l) => String(l._id) === locId);
        const stateCode = (match?.state ?? '').trim().toUpperCase();
        return /^[A-Z]{2}$/.test(stateCode) ? stateCode : null;
    }, [settings, state.editedFields.locationId, invoice?.locationId]);

    if (!invoice) return null;

    return (
        <>
            <RevertToOriginalModal
                isOpen={!!state.revertModal}
                onClose={state.closeRevertModal}
                fieldLabel={state.revertModal?.fieldLabel ?? ''}
                originalValue={state.getRevertModalOriginalValue()}
                currentValue={state.getRevertModalCurrentValue()}
                onRevert={state.handleRevertFromModal}
            />
            <InvoiceResetConfirmModal
                isOpen={state.resetConfirmOpen}
                onClose={() => state.setResetConfirmOpen(false)}
                isOriginalExtracted={!!invoice?.originalExtracted}
                isLoading={state.updateInvoiceMutation.isPending}
                onConfirm={state.handleResetConfirm}
            />
            <InvoiceReprocessPagesConfirmModal
                isOpen={reprocessPagesModalOpen}
                onClose={() => setReprocessPagesModalOpen(false)}
                isLoading={pageReplace.replaceMutation.isPending}
                previewUrls={pageReplace.modalPreviewUrls}
                onConfirm={() => {
                    const processingToastId = 'invoice-reprocess-processing';
                    setDetailTransitionMode('reprocess');
                    setReprocessPagesModalOpen(false);
                    if (toast.isActive(processingToastId)) toast.close(processingToastId);
                    toast({
                        id: processingToastId,
                        title: 'Re-processing invoice pages…',
                        description: 'This will stay visible until OCR and reconciliation finish.',
                        status: 'info',
                        duration: null,
                        isClosable: false,
                    });
                    pageReplace.replaceMutation.mutate(undefined, {
                        onSuccess: () => {
                            setVendorAliasPrompt(null);
                            setDetailTransitionMode(null);
                            if (toast.isActive(processingToastId)) toast.close(processingToastId);
                            toast({
                                title: 'Invoice re-processed',
                                description: 'OCR and reconciliation have finished with your new pages.',
                                status: 'success',
                                duration: 5000,
                                isClosable: true,
                            });
                        },
                        onError: (e: unknown) => {
                            setDetailTransitionMode(null);
                            if (toast.isActive(processingToastId)) toast.close(processingToastId);
                            toast({
                                title: 'Re-process failed',
                                description: e instanceof Error ? e.message : 'Please try again.',
                                status: 'error',
                                duration: 7000,
                                isClosable: true,
                            });
                        },
                    });
                }}
                isPdf={isPdf}
                pageCount={
                    pageReplace.modalPreviewUrls.length > 0
                        ? pageReplace.modalPreviewUrls.length
                        : isPdf
                          ? pdfPageCount
                          : allImages.length
                }
            />
            <InvoiceSubmitConfirmModal
                isOpen={state.submitConfirmOpen}
                onClose={() => state.setSubmitConfirmOpen(false)}
                isLoading={state.updateInvoiceMutation.isPending}
                onConfirm={() => {
                    state.handleSubmitConfirm();
                }}
                freightAsLineItem={alwaysShowFreightTab}
                salesTaxAsLineItem={salesTaxAsLineItem}
                payloadPreview={isReturnSlip ? undefined : state.submitPayloadPreview}
                returnSlipPreview={isReturnSlip ? state.returnSlipSubmitPreview : null}
                submitBlocked={isReturnSlip ? false : (state.submitPayloadPreview?.submitBlocked ?? false)}
                manualPoLineInfos={isReturnSlip ? [] : (state.submitManualPoLineInfos ?? [])}
                unmatchedManualLineCount={isReturnSlip ? 0 : (state.submitPayloadPreview?.unmatchedManualLineCount ?? 0)}
                manualPoHeaderSelections={state.manualPoHeaderSelections ?? {}}
                onManualPoChange={(globalIndex, poHeaderId) =>
                    state.setManualPoHeaderSelections((prev) => ({ ...prev, [globalIndex]: poHeaderId }))
                }
            />
            <InvoiceArithmeticBlockModal
                isOpen={isArithmeticBlockOpen}
                onClose={() => setIsArithmeticBlockOpen(false)}
                issues={arithmeticFlagMessages}
            />
            <InvoiceChangeInvoiceTypeModal
                isOpen={invoiceTypeChangeModal.open && invoiceTypeChangeModal.pendingType != null}
                onClose={() => setInvoiceTypeChangeModal({ open: false, pendingType: null })}
                isLoading={state.updateInvoiceMutation.isPending}
                currentType={(state.editedFields?.invoiceType ?? invoice.invoiceType ?? 'received') as InvoiceType}
                nextType={(invoiceTypeChangeModal.pendingType ?? 'received') as InvoiceType}
                onConfirm={handleConfirmInvoiceTypeChange}
            />
            <InvoiceChangeShopLocationModal
                isOpen={shopLocationChangeModal.open && shopLocationChangeModal.pendingLocationId != null}
                onClose={() =>
                    setShopLocationChangeModal({
                        open: false,
                        pendingLocationId: null,
                        pendingLocationTitle: '',
                    })
                }
                isLoading={state.updateInvoiceMutation.isPending}
                currentLocationLabel={String(
                    state.editedFields?.locationTitle ?? invoice.locationTitle ?? '',
                ).trim()}
                nextLocationLabel={shopLocationChangeModal.pendingLocationTitle}
                onConfirm={handleConfirmShopLocationChange}
            />
            <InvoiceChangeVendorModal
                isOpen={vendorChangeModal.open && vendorChangeModal.pendingVendor != null}
                onClose={() =>
                    setVendorChangeModal({ open: false, pendingVendor: null, source: null })
                }
                isLoading={state.updateInvoiceMutation.isPending}
                currentVendor={String(
                    state.editedFields?.supplierText ?? invoice.supplierText ?? '',
                ).trim()}
                nextVendor={vendorChangeModal.pendingVendor ?? ''}
                onConfirm={handleConfirmVendorChange}
            />
            <InvoiceRoChangeConfirmModal
                isOpen={roChangeConfirmOpen}
                onClose={() => setRoChangeConfirmOpen(false)}
                isLoading={state.updateInvoiceMutation.isPending}
                previousRo={state.getOriginalRoNumber()}
                nextRo={state.getCurrentRoNumber()}
                onConfirm={handleConfirmRoChangeSave}
                nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
            />
            <InvoiceTeachExtractionPromptModal
                isOpen={teachExtractionPrompt.open && teachExtractionPrompt.commit != null}
                onClose={handleCloseTeachExtractionPrompt}
                fieldLabel={teachExtractionPrompt.commit?.fieldLabel ?? 'this field'}
                extractedValue={teachExtractionPrompt.extractedValue}
                correctedValue={teachExtractionPrompt.commit?.correctedValue ?? ''}
                amountLocationHint={
                    teachExtractionPrompt.commit?.invoiceField === 'taxesTotal' ||
                    teachExtractionPrompt.commit?.invoiceField === 'orderTotal'
                }
                vendorName={extractionTeaching.vendorName}
                invoiceTypeLabel={extractionTeaching.invoiceTypeLabel}
                isLoading={rememberObservationPending}
                onConfirm={handleConfirmTeachExtractionPrompt}
            />
            <InvoicePartSuggestionModal
                isOpen={!!state.suggestionModal}
                onClose={state.closeSuggestionModal}
                suggestedPartNumber={state.suggestionModal?.suggestedPartNumber ?? ''}
                onAccept={state.handleAcceptSuggestion}
            />
            <InvoicePartNumberChoiceModal
                isOpen={!!state.partNumberChoiceModal}
                onClose={state.closePartNumberChoiceModal}
                issue={state.partNumberChoiceModal?.issue ?? null}
                onUseNexPart={state.handleUseNexPart}
            />
            <InvoiceVendorSuggestionModal
                isOpen={!!state.vendorSuggestionModal}
                onClose={state.closeVendorSuggestionModal}
                suggestedVendorName={state.vendorSuggestionModal?.suggestedVendorName ?? ''}
                onAccept={() => {
                    const suggested = state.vendorSuggestionModal?.suggestedVendorName;
                    if (!suggested) return;
                    handleVendorChangeRequest(suggested, 'suggestion');
                }}
            />
            <InvoiceUnmatchConfirmModal
                isOpen={!!state.unmatchConfirmModal}
                onClose={() => state.setUnmatchConfirmModal(null)}
                partNumber={state.unmatchConfirmModal?.partNumber}
                nexPartNumber={state.unmatchConfirmModal?.nexPartNumber}
                onConfirm={() => {
                    if (state.unmatchConfirmModal) {
                        const idx = state.unmatchConfirmModal.partIndex;
                        const unmatchedNexLine = state.unmatchConfirmModal.nexLine;
                        state.setUnmatchedPartIndices((prev: Set<number>) => { const next = new Set(prev); next.add(idx); return next; });
                        state.setLocalMatchedNexOverrides((prev: Record<number, any>) => { const next = { ...prev }; delete next[idx]; return next; });
                        if (unmatchedNexLine) {
                            state.setPreviouslyMatchedNexLines((prev: Record<number, any>) => ({ ...prev, [idx]: unmatchedNexLine }));
                        }
                        const cieca =
                            typeof unmatchedNexLine?.ciecaCode === 'string'
                                ? unmatchedNexLine.ciecaCode
                                : undefined;
                        const partType =
                            typeof unmatchedNexLine?.partType === 'string'
                                ? unmatchedNexLine.partType
                                : undefined;
                        const partKind = pricingPartKindFromNexLine({ partType, ciecaCode: cieca });
                        const loc = resolveTabularLocationForGlobalPartIndex(
                            idx,
                            invoice,
                            state.editedFields,
                        );
                        if (loc?.kind === 'line') {
                            const linePart = (
                                state.editedFields.lineItems ??
                                invoice.lineItems ??
                                []
                            )[loc.localIndex];
                            if (linePart) {
                                const resolved = resolveListPriceAfterManualLkqMatch(linePart, cieca, {
                                    invoiceType: state.editedFields.invoiceType ?? invoice.invoiceType,
                                    lkqListPriceMarkup:
                                        state.editedFields.lkqListPriceMarkup ?? invoice.lkqListPriceMarkup,
                                    lkqListPriceDiscount:
                                        state.editedFields.lkqListPriceDiscount ?? invoice.lkqListPriceDiscount,
                                    invoicingSettings,
                                    insuranceCompanyId: invoice.internalInsuranceCompanyId ?? null,
                                    supplierId: invoice.internalSupplierId ?? null,
                                    locationState,
                                    partKind,
                                    vehicleMake: invoice.vehicleMake ?? null,
                                });
                                const matchChangedBuy =
                                    resolved?.mode === 'discount' || resolved?.mode === 'billAtCost';
                                if (partKind || resolved?.mode === 'billAtCost') {
                                    const restored = computeListPriceAfterManualUnmatch(linePart);
                                    if (restored !== Number(linePart.listPrice ?? 0)) {
                                        state.updateLineItem(loc.localIndex, 'listPrice', restored);
                                    }
                                }
                                if (matchChangedBuy) {
                                    const previousUnitBuyPrice = state.effectiveReconciliationMatchNotes.find(
                                        (note) => note.partIndex === idx,
                                    )?.extractedFromPage?.unitBuyPrice;
                                    const restoredBuy = buyPriceAfterManualUnmatch({
                                        part: linePart,
                                        matchChangedBuy,
                                        previousUnitBuyPrice,
                                    });
                                    if (
                                        restoredBuy != null &&
                                        Math.abs(restoredBuy - Number(linePart.unitBuyPrice ?? 0)) >= 0.005
                                    ) {
                                        state.updateLineItem(loc.localIndex, 'unitBuyPrice', restoredBuy);
                                    }
                                }
                            }
                        }
                        state.setLocalReconciliationMatchNotes((prev) => {
                            if (!(idx in prev)) return prev;
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                        });
                    }
                    state.setUnmatchConfirmModal(null);
                }}
            />
            <SaveVendorNexAliasPromptModal
                isOpen={!!vendorAliasPrompt}
                onClose={() => setVendorAliasPrompt(null)}
                context={vendorAliasPrompt}
                isSaving={updateSettingsMutation.isPending}
                onConfirm={async (toAdd) => {
                    if (!toAdd.length || !vendorAliasPrompt) {
                        setVendorAliasPrompt(null);
                        return;
                    }
                    const sessionId = sessionContext?.sessionId;
                    if (!sessionId) {
                        toast({
                            title: 'Could not save aliases',
                            description: 'Your session expired. Refresh and try again.',
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                        });
                        return;
                    }
                    const base = readFullInvoicingSettingsFromSections(
                        settings?.topSection?.sections,
                        settings?.bottomSection?.sections,
                    );
                    const nexVendorName = toAdd[0]?.nexVendorName?.trim() ?? '';
                    const freshInvoice =
                        (id
                            ? queryClient.getQueryData<InvoiceDetailResponse>(
                                  invoiceDetailQueryKey(id),
                              )
                            : undefined) ?? invoice;
                    const nexMeta = findNexPoVendorMetaForName(
                        freshInvoice?.nexResponse,
                        nexVendorName
                    );
                    try {
                        const merged = await prepareInvoiceVendorAliasMappingsSave({
                            mappings: base.vendorNexAliasMappings ?? [],
                            supplierId:
                                vendorAliasPrompt.internalSupplierId ??
                                freshInvoice?.internalSupplierId,
                            nexVendorId: vendorAliasPrompt.nexVendorId ?? nexMeta?.vendorId,
                            nexVendorName,
                            invoiceAliasTexts: toAdd.map((row) => row.invoiceAlias),
                            sessionId,
                        });
                        if (!merged.ok) {
                            const debugVendorId =
                                vendorAliasPrompt.nexVendorId ?? nexMeta?.vendorId ?? null;
                            const debugSupplierId =
                                vendorAliasPrompt.internalSupplierId ??
                                freshInvoice?.internalSupplierId ??
                                null;
                            toast({
                                title: 'Could not link supplier for this alias',
                                description:
                                    debugVendorId != null
                                        ? `No Yaxle supplier found for Nexsyis vendor ID ${debugVendorId}${debugSupplierId ? ` (invoice supplier ${debugSupplierId})` : ''}. Ask an admin to link the supplier in the catalog.`
                                        : 'Could not read the Nexsyis vendor ID from this RO. Refresh Nexsyis data and try again.',
                                status: 'warning',
                                duration: 8000,
                                isClosable: true,
                            });
                            return;
                        }
                        updateSettingsMutation.mutate(
                            {
                                invoicingSettings: buildVendorAliasMappingsOnlyPayload(merged.mappings),
                                ...(id ? { activityContext: { invoiceId: id } } : {}),
                            },
                            {
                                onSuccess: () => {
                                    queryClient.invalidateQueries({
                                        queryKey: ['settings', sessionContext?.organizationId ?? ''],
                                    });
                                    if (id) {
                                        queryClient.invalidateQueries({
                                            queryKey: ['/invoice/activity-logs', id],
                                        });
                                    }
                                    toast({
                                        title: 'Vendor aliases saved',
                                        status: 'success',
                                        duration: 3000,
                                        isClosable: true,
                                    });
                                    setVendorAliasPrompt(null);
                                },
                                onError: (error) => {
                                    const apiMessage = extractUpdateSettingsErrorMessage(error);
                                    toast({
                                        title: 'Could not save aliases',
                                        description:
                                            apiMessage ??
                                            'Something went wrong while saving organization settings.',
                                        status: 'error',
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                },
                            }
                        );
                    } catch {
                        toast({
                            title: 'Could not save aliases',
                            description: 'Something went wrong while resolving the supplier.',
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                        });
                    }
                }}
            />
            <InvoiceReconciliationDetailModal
                isOpen={!!state.reconciliationDetailModal}
                onClose={state.closeReconciliationDetailModal}
                reconciliationDetailModal={state.reconciliationDetailModal}
                invoice={invoice}
                editedFields={state.editedFields}
                getIssuesForPart={state.getIssuesForPart}
                getPartNumberChoiceIssue={state.getPartNumberChoiceIssue}
                getIssueTooltipText={state.getIssueTooltipText}
                unmatchedPartIndices={state.unmatchedPartIndices}
                previouslyMatchedNexLines={state.previouslyMatchedNexLines}
                localMatchedNexOverrides={state.localMatchedNexOverrides}
                selectedNexOptionKey={state.selectedNexOptionKey}
                setSelectedNexOptionKey={state.setSelectedNexOptionKey}
                selectedNexOptionAccordionKey={state.selectedNexOptionAccordionKey}
                setSelectedNexOptionAccordionKey={state.setSelectedNexOptionAccordionKey}
                setLocalMatchedNexOverrides={state.setLocalMatchedNexOverrides}
                setLocalReconciliationMatchNotes={state.setLocalReconciliationMatchNotes}
                setUnmatchedPartIndices={state.setUnmatchedPartIndices}
                setPreviouslyMatchedNexLines={state.setPreviouslyMatchedNexLines}
                updateLineItem={state.updateLineItem}
                updateField={state.updateField}
                getLineItemStringValue={state.getLineItemStringValue}
                renderLineItemNumericCell={state.renderLineItemNumericCell}
                updateIgnoredPart={state.updateIgnoredPart}
                getIgnoredPartStringValue={state.getIgnoredPartStringValue}
                renderIgnoredPartNumericCell={state.renderIgnoredPartNumericCell}
                hasChanges={state.hasChanges}
                effectiveMatchedNexLines={state.effectiveMatchedNexLines}
                lineItemBackendIndexByCurrentIndex={state.lineItemBackendIndexByCurrentIndex}
                onAcceptVendorFromMismatch={
                    isReadOnly
                        ? undefined
                        : (vendorName) => handleVendorChangeRequest(vendorName, 'mismatch')
                }
                onRefreshNexsyisData={!isReadOnly ? state.handleRefreshNexsyisData : undefined}
                isRefreshNexsyisPending={state.updateInvoiceMutation.isPending || state.invoiceUpdateInProgress}
                isReadOnly={isReadOnly}
                nexLineQuantityBasis={nexLineQuantityBasis ?? 'qtyRec'}
                invoicingSettings={invoicingSettings}
                locationState={locationState}
                vehicleMake={invoice.vehicleMake ?? null}
            />

        </>
    );
}
